import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAppContext } from "../../context/AppContext";
import { getPassTypes } from "../../services/passService";
import { ScheduleTrans } from "../../utils/translations";
import { reviewsData } from "../utilPages/reviews";
import GiftPurchaseModal from "../Schedule/GiftPurchaseModal";
import "./WomensDayGift.css";

const CONFIG = {
  whatsappPhone: "972559116975",
  contactEmail: process.env.REACT_APP_CONTACT_EMAIL || "joykayoga@gmail.com",
  instagram: "https://www.instagram.com/joykayoga/", 
  instructorPhoto: "https://res.cloudinary.com/deaqcrhah/image/upload/v1772548214/21_1_of_1_kjh7ia.jpg", // image URL or path
  instructorName: "Anya Joy",
  instructorBio: "Я знаю, каково жить в напряжении — и знаю, как йога меняет это изнутри. Три года помогаю людям найти практику, которая работает именно для их тела. Занятия на русском, маленькие группы, без фанатизма — только то, что реально меняет самочувствие.",
};

const whatsappUrl = CONFIG.whatsappPhone.startsWith("[")
  ? "#"
  : `https://wa.me/${CONFIG.whatsappPhone.replace(/\D/g, "")}`;

const GIFT_PASS_NAMES = ["basic", "standard", "single"];

const MARCH_8_2026 = new Date(2026, 2, 8, 0, 0, 0, 0);

function getCountdown(target) {
  const now = new Date();
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds };
}

function getDaysLabel(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "дня";
  return "дней";
}

export default function WomensDayGift() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const giftRef = useRef(null);
  const purchaseRef = useRef(null);
  const mainRef = useRef(null);
  const { setToaster, language } = useAppContext();
  const lang = language || "russian";

  const [passTypes, setPassTypes] = useState([]);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showErrorContactModal, setShowErrorContactModal] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [countdown, setCountdown] = useState(() => getCountdown(MARCH_8_2026));
  const [reviewIndex, setReviewIndex] = useState(0);

  useEffect(() => {
    if (searchParams.get("gift") === "success") {
      const orderId = searchParams.get("orderId");
      navigate(`/success?from=gift${orderId ? `&orderId=${orderId}` : ""}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const details = ScheduleTrans[lang];
  const reviewsList = reviewsData[lang]?.length ? reviewsData[lang] : reviewsData.russian;

  const prevReview = () => {
    setReviewIndex((prev) => (prev === 0 ? reviewsList.length - 1 : prev - 1));
  };
  const nextReview = () => {
    setReviewIndex((prev) => (prev === reviewsList.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const passTypesRes = await getPassTypes();
        console.log(passTypesRes)
        setPassTypes(Array.isArray(passTypesRes) ? passTypesRes : []);
      } catch {
        setPassTypes([]);
      }
    };
    fetchData();
  }, []);

  // Accepts either a pass type object (preferred, reads name from the DB) or a raw name string
  const translatePassName = (passTypeOrName) => {
    const pt = passTypeOrName && typeof passTypeOrName === "object" ? passTypeOrName : null;
    const name = pt ? pt.name : passTypeOrName;
    if (pt) {
      const dbName = lang === "russian" ? (pt.displayNameRu || pt.displayName) : pt.displayName;
      if (dbName) return dbName;
    }
    const passMap = {
      trial: details.trial,
      park: details.park,
      single: details.dropInClass,
      basic: details.basicPass,
      standard: details.standartPass,
      limitless: details.limitless,
    };
    return passMap[name] || name;
  };

  const getFilteredPassTypesForGift = () => {
    const list = Array.isArray(passTypes) ? passTypes : [];
    const filtered = list.filter((p) => p && GIFT_PASS_NAMES.includes((p.name || "").toLowerCase()));
    return [...filtered].sort((a, b) => (a.basePrice || 0) - (b.basePrice || 0));
  };

  const getActiveDiscount = (passType) => {
    const now = new Date();
    return passType.discounts?.find(
      (d) => d.active && new Date(d.validFrom) <= now && new Date(d.validTo) >= now
    );
  };

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  useEffect(() => {
    const root = mainRef.current;
    if (!root) return;
    const sections = root.querySelectorAll(".womensday-section, .womensday-footer");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("womensday-visible");
        });
      },
      { rootMargin: "0px 0px -60px 0px", threshold: 0.1 }
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const tick = () => {
      const next = getCountdown(MARCH_8_2026);
      setCountdown(next);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (ref) => {
    ref?.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>Подарок на 8 марта — Йога в Рамат Гане</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <main ref={mainRef} className="womensday-page min-h-screen overflow-x-hidden font-sans text-[#2c2925] bg-[#faf8f5]">
        {/* 1. Hero — full-screen, emotional */}
        <section className="womensday-hero relative min-h-screen min-h-[100dvh] flex items-center justify-center py-8 px-6">
          <div className="womensday-hero-noise absolute inset-0 opacity-[0.03] pointer-events-none" aria-hidden="true" />
          <div className="womensday-hero-content relative z-[2] text-center">
            <h1 className="womensday-hero-title womensday-font-serif font-semibold text-[#2c2925] m-0 mb-2 tracking-tight leading-tight" style={{ fontSize: 'clamp(2.5rem, 10vw, 4.5rem)' }}>Подари ей йогу</h1>
            <p className="womensday-hero-sub text-[#5c564e] mb-6 mx-auto max-w-[22ch]" style={{ fontSize: 'clamp(1.1rem, 4vw, 1.5rem)' }}>Место, где можно выдохнуть</p>
            {countdown && (
              <div className="flex flex-row justify-center items-center gap-4 mb-8" style={{ gap: 'clamp(0.75rem, 3vw, 1.5rem)' }} aria-label="До 8 марта">
                <div className="flex flex-col items-center min-w-[2.5rem]">
                  <span className="womensday-font-serif font-semibold text-[#2c2925] leading-tight" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)' }}>{countdown.days}</span>
                  <span className="text-[0.65rem] font-medium text-[#5c564e] uppercase tracking-widest mt-1">{getDaysLabel(countdown.days)}</span>
                </div>
                <div className="flex flex-col items-center min-w-[2.5rem]">
                  <span className="womensday-font-serif font-semibold text-[#2c2925] leading-tight" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)' }}>{countdown.hours}</span>
                  <span className="text-[0.65rem] font-medium text-[#5c564e] uppercase tracking-widest mt-1">часов</span>
                </div>
                <div className="flex flex-col items-center min-w-[2.5rem]">
                  <span className="womensday-font-serif font-semibold text-[#2c2925] leading-tight" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)' }}>{countdown.minutes}</span>
                  <span className="text-[0.65rem] font-medium text-[#5c564e] uppercase tracking-widest mt-1">минут</span>
                </div>
                <div className="flex flex-col items-center min-w-[2.5rem]">
                  <span className="womensday-font-serif font-semibold text-[#2c2925] leading-tight" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)' }}>{countdown.seconds}</span>
                  <span className="text-[0.65rem] font-medium text-[#5c564e] uppercase tracking-widest mt-1">секунд</span>
                </div>
              </div>
            )}
            <button
              type="button"
              className="womensday-cta-primary font-sans text-base font-semibold text-[#faf8f5] bg-[#c67b5c] border-none py-3.5 px-7 rounded-full cursor-pointer transition-all duration-200 hover:bg-[#d49a82] hover:-translate-y-0.5 active:translate-y-0 shadow-[0_4px_20px_rgba(198,123,92,0.35)]"
              onClick={() => scrollTo(giftRef)}
            >
              Подарить абонемент →
            </button>
          </div>
          <div className="womensday-hero-shape womensday-hero-shape--1 absolute rounded-full blur-[60px] opacity-40 pointer-events-none w-[min(70vw,400px)] h-[min(70vw,400px)] bg-[#d49a82] -top-[10%] -right-[15%]" />
          <div className="womensday-hero-shape womensday-hero-shape--2 absolute rounded-full blur-[60px] opacity-40 pointer-events-none w-[min(50vw,280px)] h-[min(50vw,280px)] bg-[#a8b896] bottom-[10%] -left-[10%]" />
        </section>

        {/* 2. The gift block */}
        <section ref={giftRef} className="womensday-section womensday-gift py-16 px-6">
          <div className="womensday-container max-w-[36rem] mx-auto">
            <h2 className="womensday-heading womensday-font-serif font-semibold text-[#2c2925] mb-7 text-center" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.25rem)' }}>Что в подарке</h2>
            <ul className="womensday-gift-list list-none p-0 m-0 mb-4 text-left">
              <li className="relative pl-6 mb-3 text-[#5c564e] text-[1.05rem] leading-normal">Разовое занятие или абонемент на месяц групповой йоги в студии</li>
              <li className="relative pl-6 mb-3 text-[#5c564e] text-[1.05rem] leading-normal">
                Занятия в студии-мамаде, Рамат Ган,{" "}
                <a
                  href="https://maps.app.goo.gl/oj4wuXf9hcWV5HQd8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="womensday-gift-link text-[#8a9b76] underline underline-offset-[3px] font-medium transition-colors hover:text-[#c67b5c]"
                >
                  Жаботински, 103
                </a>
                {" "}или онлайн
              </li>
              <li className="relative pl-6 mb-3 text-[#5c564e] text-[1.05rem] leading-normal">Небольшие группы, уютная атмосфера</li>
              <li className="relative pl-6 mb-3 text-[#5c564e] text-[1.05rem] leading-normal">Подходит для любого уровня</li>
            </ul>
            <p className="text-[0.95rem] text-[#5c564e] mb-4 italic">Абонемент действителен до конца года в течение месяца после активации.</p>
          </div>
        </section>

        {/* 2b. Pass options with prices */}
        <section className="womensday-section womensday-pass-options-section py-16 px-6">
          <div className="womensday-container max-w-[36rem] mx-auto">
            <h2 className="womensday-heading womensday-font-serif font-semibold text-[#2c2925] mb-7 text-center" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.25rem)' }}>Пакеты на выбор</h2>
            <div className="womensday-pass-options mt-4">
              {(() => {
                const filtered = getFilteredPassTypesForGift();
                return filtered.length > 0 ? (
                filtered.map((pass) => {
                  const discount = getActiveDiscount(pass);
                  const price = discount
                    ? Math.round(pass.basePrice * (1 - discount.discountPercent / 100))
                    : pass.basePrice;
                  const desc = (lang === "russian" ? pass.descriptionRu : pass.descriptionEn) || "";
                  return (
                    <div key={pass._id} className="flex items-start justify-between gap-4 py-3.5 border-b border-black/[0.08] last:border-none">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="font-semibold text-base text-[#2c2925]">
                          {translatePassName(pass)}
                        </span>
                        {desc && (
                          <span className="text-[0.85rem] text-[#5c564e] leading-[1.35]">{desc}</span>
                        )}
                      </div>
                      <div className="flex items-baseline gap-2 shrink-0">
                        {discount && (
                          <span className="text-[0.9rem] text-[#5c564e] line-through">{pass.basePrice} ₪</span>
                        )}
                        <span className="womensday-font-serif font-semibold text-xl text-[#c67b5c]">{price} ₪</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-[0.95rem] text-[#5c564e] mt-2 py-2">
                  {passTypes.length === 0 ? "Загрузка пакетов…" : "Пакеты можно выбрать при нажатии «Оплатить онлайн»."}
                </p>
              );
              })()}
            </div>
          </div>
        </section>

        {/* 3. Why this gift */}
        <section className="womensday-section womensday-why py-16 px-6 bg-[#f5f0e8]">
          <div className="womensday-container max-w-[36rem] mx-auto">
            <h2 className="womensday-heading womensday-font-serif font-semibold text-[#2c2925] mb-7 text-center" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.25rem)' }}>Почему этот подарок</h2>
            <ul className="list-none p-0 m-0 text-left">
              <li className="flex items-start gap-3 py-5 border-b border-black/[0.08] last:border-none text-[1.05rem] leading-normal text-[#5c564e]">
                <span className="text-[#8a9b76] text-base shrink-0">✦</span>
                Она наконец сделает что-то для себя
              </li>
              <li className="flex items-start gap-3 py-5 border-b border-black/[0.08] last:border-none text-[1.05rem] leading-normal text-[#5c564e]">
                <span className="text-[#8a9b76] text-base shrink-0">✦</span>
                Место, где можно успокоиться и забыть о новостях
              </li>
              <li className="flex items-start gap-3 py-5 border-b border-black/[0.08] last:border-none text-[1.05rem] leading-normal text-[#5c564e]">
                <span className="text-[#8a9b76] text-base shrink-0">✦</span>
                Безопасно, студия - защищенная комната (мамад)
              </li>
            </ul>
          </div>
        </section>

        {/* Testimonial */}
        <section className="womensday-section womensday-testimonial py-16 px-6 bg-[rgba(138,155,118,0.08)]">
          <div className="womensday-container max-w-[36rem] mx-auto relative px-10">
            <button
              type="button"
              onClick={prevReview}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 p-0 border-none bg-transparent text-[#5c564e] text-[1.75rem] leading-none cursor-pointer transition-colors hover:text-[#8a9b76]"
              aria-label="Предыдущий отзыв"
            >
              ‹
            </button>
            <blockquote className="m-0 p-0 border-none text-center">
              <p className="womensday-quote-text text-[1.1rem] leading-[1.6] text-[#5c564e] italic py-0 px-6 relative">
                {reviewsList[reviewIndex].text}
              </p>
              <cite className="block text-[0.9rem] text-[#5c564e] not-italic mt-3">— {reviewsList[reviewIndex].name}</cite>
            </blockquote>
            <button
              type="button"
              onClick={nextReview}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 p-0 border-none bg-transparent text-[#5c564e] text-[1.75rem] leading-none cursor-pointer transition-colors hover:text-[#8a9b76]"
              aria-label="Следующий отзыв"
            >
              ›
            </button>
            <div className="flex justify-center gap-2 mt-4">
              {reviewsList.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setReviewIndex(index)}
                  className={`w-2 h-2 p-0 border-none rounded-full cursor-pointer transition-colors ${
                    index === reviewIndex ? "bg-[#8a9b76]" : "bg-[#a8b896] hover:bg-[#8a9b76]"
                  }`}
                  aria-label={`Отзыв ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* 4. Special practice 8 March */}
        <section className="womensday-section womensday-march8 py-16 px-6">
          <div className="womensday-container max-w-[36rem] mx-auto">
            <h2 className="womensday-heading womensday-font-serif font-semibold text-[#2c2925] mb-7 text-center" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.25rem)' }}>8 марта</h2>
            <p className="text-[1.1rem] leading-[1.6] text-[#5c564e] m-0 text-center max-w-[28rem] mx-auto">
              8 марта — особая практика: <strong className="text-[#2c2925] font-semibold">фракционная шавасана</strong> для глубокого расслабления.
            </p>
          </div>
        </section>

        {/* 5. About the instructor */}
        <section className="womensday-section womensday-instructor py-16 px-6">
          <div className="womensday-container max-w-[36rem] mx-auto flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:max-w-[28rem]">
            <div className="shrink-0">
              {CONFIG.instructorPhoto.startsWith("[") ? (
                <div className="w-[140px] h-[140px] rounded-full bg-[#f5f0e8] border-2 border-dashed border-[#a8b896] flex items-center justify-center text-xs text-[#5c564e] text-center p-2">[INSTRUCTOR_PHOTO]</div>
              ) : (
                <img
                  src={CONFIG.instructorPhoto}
                  alt={CONFIG.instructorName}
                  className="w-[140px] h-[140px] rounded-full object-cover border-[3px] border-[#a8b896]"
                />
              )}
            </div>
            <div className="text-center w-fit mx-auto sm:text-left">
              <h2 className="womensday-font-serif font-semibold text-2xl text-[#2c2925] mb-2 sm:text-left text-center">{CONFIG.instructorName}</h2>
              <p className="text-[0.95rem] text-[#5c564e] m-0 leading-normal">{CONFIG.instructorBio}</p>
            </div>
          </div>
        </section>

        {/* 6. Purchase */}
        <section ref={purchaseRef} className="womensday-section womensday-purchase py-16 px-6 bg-[#faf8f5]">
          <div className="womensday-container max-w-[36rem] mx-auto">
            <h2 className="womensday-heading womensday-font-serif font-semibold text-[#2c2925] mb-7 text-center" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.25rem)' }}>Как подарить</h2>
            <div className="flex flex-col gap-4 mb-5">
              <button
                type="button"
                className="womensday-btn-primary inline-block text-center font-sans text-base font-semibold py-4 px-6 rounded-full no-underline transition-all duration-200 border-2 border-transparent bg-[#c67b5c] text-[#faf8f5] shadow-[0_4px_16px_rgba(198,123,92,0.3)] hover:bg-[#d49a82] hover:-translate-y-0.5"
                onClick={() => {
                  const available = getFilteredPassTypesForGift();
                  if (available.length > 0) {
                    setSelectedPass(available[0]);
                    setShowGiftModal(true);
                  } else {
                    setToaster({
                      message: lang === "russian" ? "Пакеты пока недоступны. Напишите в WhatsApp." : "Passes not available. Contact us on WhatsApp.",
                      type: "info",
                      show: true,
                    });
                  }
                }}
              >
                Оплатить онлайн
              </button>
              <a
                href={whatsappUrl}
                className="inline-block text-center font-sans text-base font-semibold py-4 px-6 rounded-full no-underline transition-all duration-200 border-2 border-[#8a9b76] bg-transparent text-[#8a9b76] hover:bg-[rgba(138,155,118,0.12)] hover:-translate-y-0.5"
                target="_blank"
                rel="noopener noreferrer"
              >
                Написать в WhatsApp
              </a>
            </div>
            <p className="text-[0.85rem] italic text-[#5c564e] mt-4 text-center">
              Не подошло — абонемент можно передать подруге или вернуть</p>
            <p className="text-[0.9rem] text-[#5c564e] mt-2 text-center">
              Подтверждение придёт на почту сразу после оплаты — можно распечатать и вручить
            </p>
          </div>
        </section>

      </main>

      <GiftPurchaseModal
        show={showGiftModal}
        onClose={() => setShowGiftModal(false)}
        passTypes={getFilteredPassTypesForGift()}
        selectedPass={selectedPass}
        setSelectedPass={setSelectedPass}
        getActiveDiscount={getActiveDiscount}
        translatePassName={translatePassName}
        language={lang}
        onError={(message) => {
          setShowGiftModal(false);
          setToaster({ message, type: "error", show: true });
          setShowErrorContactModal(true);
        }}
      />

      {/* Error contact modal — when something goes wrong */}
      {showErrorContactModal && (
        <div
          className="fixed inset-0 bg-[rgba(44,41,37,0.5)] flex items-center justify-center z-[100] p-4 animate-[womensday-fade-in_0.2s_ease-out]"
          onClick={() => setShowErrorContactModal(false)}
          onKeyDown={(e) => e.key === "Escape" && setShowErrorContactModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gift-error-title"
        >
          <div className="bg-[#faf8f5] rounded-[1.25rem] max-w-[24rem] w-full py-6 px-6 pb-7 relative shadow-[0_20px_40px_rgba(44,41,37,0.2)] animate-[womensday-fade-in_0.25s_ease-out]" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="absolute top-3 right-3 bg-transparent border-none text-[#5c564e] text-xl cursor-pointer p-1 leading-none transition-colors hover:text-[#2c2925]" onClick={() => setShowErrorContactModal(false)} aria-label="Закрыть">✕</button>
            <h2 id="gift-error-title" className="womensday-font-serif font-semibold text-2xl text-[#2c2925] mb-3 pr-6">Что-то пошло не так</h2>
            <p className="text-[0.95rem] text-[#5c564e] mb-5 leading-normal">
              Напишите нам на почту или в WhatsApp — поможем оформить подарок.
            </p>
            <div className="flex flex-wrap gap-3 mb-5">
              <a href={`mailto:${CONFIG.contactEmail}`} className="inline-flex items-center py-2 px-4 rounded-full text-[0.9rem] font-semibold no-underline transition-colors bg-[#f5f0e8] text-[#c67b5c] border-2 border-[#d49a82] hover:bg-[#d49a82] hover:text-[#faf8f5]">
                {CONFIG.contactEmail}
              </a>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center py-2 px-4 rounded-full text-[0.9rem] font-semibold no-underline transition-colors bg-[#25d366] text-white border-2 border-[#20bd5a] hover:bg-[#20bd5a]">
                WhatsApp
              </a>
            </div>
            <button type="button" className="w-full font-sans text-base font-semibold text-[#faf8f5] bg-[#c67b5c] border-none py-3 px-5 rounded-full cursor-pointer transition-colors hover:bg-[#d49a82]" onClick={() => setShowErrorContactModal(false)}>Закрыть</button>
          </div>
        </div>
      )}
    </>
  );
}
